import { useNavigate } from "react-router-dom";
import {
  Landmark,
  Building2,
  Scale,
  Shield,
  Leaf,
  FileSearch,
  ArrowLeft,
  CheckCircle2,
} from "lucide-react";

const courtOptions = [
  {
    id: "supreme",
    title: "Supreme Court",
    description: "Search cases from the Supreme Court.",
    route: "/portal/fetchfromsupremecourt",
    icon: Scale,
  },
  {
    id: "high",
    title: "High Court",
    description: "Search cases from the High Court.",
    route: "/portal/fetchfromhighcourt",
    icon: Landmark,
  },
  {
    id: "district",
    title: "District Court",
    description: "Search cases from the District Court.",
    route: "/portal/fetchfromdistrictcourt",
    icon: Building2,
  },
  {
    id: "nclt",
    title: "NCLT Cases",
    description: "Search cases from the National Company Law Tribunal.",
    route: "/portal/fetchfromnclt",
    icon: Shield,
  },
  {
    id: "rera",
    title: "RERA Cases",
    description: "Search cases from the Real Estate Regulatory Authority.",
    route: "/portal/fetchfromrera",
    icon: Building2,
  },
  {
    id: "ngt",
    title: "NGT Cases",
    description: "Search cases from the National Green Tribunal.",
    route: "/portal/fetchfromngt",
    icon: Leaf,
  },
  {
    id: "escr",
    title: "eSCR Judgment and Order",
    description: "Search judgments and orders from the eSCR database.",
    route: "/portal/fetchfromescr",
    icon: FileSearch,
  },
  {
    id: "consumer",
    title: "Consumer Court (e-Jagriti)",
    description: "Search consumer cases from e-Jagriti.",
    route: "/portal/fetchfromejagriti",
    icon: Landmark,
  },
];

function Stepper() {
  const steps = ["Select Court", "Search Case", "Results", "Import Case"];

  return (
    <div className="mb-8">
      <div className="flex flex-wrap items-center gap-y-3">
        {steps.map((step, index) => {
          const isActive = index === 0;
          const isCompleted = false;

          return (
            <div key={step} className="flex items-center">
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-all ${
                    isActive
                      ? "bg-[#4F46E5] text-white shadow-sm"
                      : isCompleted
                      ? "bg-emerald-500 text-white"
                      : "border border-slate-300 bg-white text-slate-500"
                  }`}
                >
                  {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
                </div>

                <span
                  className={`text-sm font-medium ${
                    isActive ? "text-slate-900" : "text-slate-500"
                  }`}
                >
                  {step}
                </span>
              </div>

              {index !== steps.length - 1 && (
                <div className="mx-4 h-px w-10 bg-slate-300 sm:w-16" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CourtCard({ option, onSelect }) {
  const Icon = option.icon;

  return (
    <button
      type="button"
      onClick={() => onSelect(option)}
      className="group relative flex h-[145px] w-full flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-6 text-center shadow-none transition-all duration-200 hover:border-[#5B5AF7] hover:shadow-[0_12px_30px_rgba(91,90,247,0.12)]"
    >
      {/* icon */}
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 transition-colors group-hover:bg-[#EEF0FF]">
        <Icon className="h-6 w-6 text-slate-700 transition-colors group-hover:text-[#5B5AF7]" />
      </div>

      {/* title */}
      <h3 className="max-w-[170px] text-[16px] font-semibold leading-6 text-slate-900">
        {option.title}
      </h3>
    </button>
  );
}

export default function FindCaseOptions() {
  const navigate = useNavigate();

  // Selecting a court goes straight to its search screen — no Continue step.
  const handleSelect = (option) => {
    if (option?.route) navigate(option.route);
  };

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      <main className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto w-full max-w-[1320px]">
          {/* Header */}
          <div className="mb-6 flex items-start gap-4">
            <button
              type="button"
              onClick={() => navigate("/portal/dashboard")}
              className="mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>

            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                Find Cases
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                Download and import cases from courts
              </p>
            </div>
          </div>

          {/* Main card */}
          <section className="rounded-[28px] border border-slate-200 bg-white px-5 py-6 shadow-[0_24px_60px_rgba(15,23,42,0.08)] sm:px-8 sm:py-8 lg:px-10">
            

            <div className="mb-6">
              <h2 className="text-[16px] font-semibold text-slate-900">
                Select Court Level
              </h2>
            </div>

            {/* Court grid */}
            <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
              {courtOptions.map((option) => (
                <CourtCard
                  key={option.id}
                  option={option}
                  onSelect={handleSelect}
                />
              ))}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}